import {useCallback} from 'react';

import {
  GithubAuthProvider,
  OAuthProvider,
  fetchSignInMethodsForEmail,
  getAuth,
  linkWithCredential,
  signInWithPopup,
} from 'firebase/auth';
import {useTranslation} from 'react-i18next';
import {FaGithub} from 'react-icons/fa';

import type {ErrorDto} from 'types/Dto/Error';

import {EVENTS} from 'constants/mixpanel';

import useSession from 'hooks/core/useSession';
import useMixpanel from 'hooks/useMixpanel';

import {Button} from 'components/v3/button';
import {useToast} from 'components/v3/toast/use-toast';

import {isCancelledCode, useAuth, type FirebaseError} from '../..';
import {SUPPORTED_POPUP_SIGN_IN_METHODS, getProvider} from '../utils';

const GithubAuthButton = () => {
  const {track} = useMixpanel();
  const {setLoading} = useAuth();
  const {doLogin} = useSession();
  const {toast} = useToast();
  const {t} = useTranslation();

  const onPress = useCallback(async () => {
    const auth = getAuth();
    try {
      const provider = new GithubAuthProvider();

      const result = await signInWithPopup(auth, provider);

      setLoading(true);

      const {is_new} = await doLogin({firebaseUser: result.user});

      track(EVENTS.LOGIN);

      if (is_new) {
        track(EVENTS.SIGN_UPS);
      }
    } catch (err: any) {
      // if (err.code === 'auth/account-exists-with-different-credential') {
      //   const credential = OAuthProvider.credentialFromResult(
      //     err.customData,
      //   ) as OAuthCredential;

      //   debugger;
      // }

      if (
        err?.customData?.email &&
        err.code === 'auth/account-exists-with-different-credential'
      ) {
        const providers = await fetchSignInMethodsForEmail(
          auth,
          err?.customData?.email,
        );

        let firstPopupProviderMethod = providers.find(p =>
          SUPPORTED_POPUP_SIGN_IN_METHODS.includes(p as any),
        );

        if (!firstPopupProviderMethod) {
          const verifiedProvider =
            err?.customData?._tokenResponse?.verifiedProvider ?? [];

          firstPopupProviderMethod = verifiedProvider.find((p: any) =>
            SUPPORTED_POPUP_SIGN_IN_METHODS.includes(p),
          );
        }

        // Test: Could this happen with email link then trying social provider?
        if (!firstPopupProviderMethod) {
          throw new Error(
            `Your account is linked to a provider that isn't supported.`,
          );
        }

        const linkedProvider = getProvider(firstPopupProviderMethod);
        linkedProvider.setCustomParameters({
          login_hint: err?.customData?.email,
        });

        const result = await signInWithPopup(auth, linkedProvider);

        const credential = OAuthProvider.credentialFromError(err);

        if (!credential) {
          return;
        }

        await linkWithCredential(result.user, credential);

        const {is_new} = await doLogin({firebaseUser: result.user});
        track(EVENTS.LOGIN);

        if (is_new) {
          track(EVENTS.SIGN_UPS);
        }

        setLoading(false);

        return;
      }

      setLoading(false);

      if (isCancelledCode(err as FirebaseError)) {
        return;
      }

      console.error(err);

      toast({
        variant: 'destructive',
        title: 'Error',
        description: t([
          `errors.${(err as FirebaseError)?.code}`,
          `errors.${(err as ErrorDto).message}`,
          'pages.login.error',
        ]),
      });
    }
  }, [setLoading, toast, t, track, doLogin]);

  return (
    // <Button
    //   className="dark-blue w-72 ring-[0.5px] ring-zinc-500 flex items-center justify-center bg-nr-gray-950 px-8 py-2"
    //   onPress={onPress}
    //   label="auth.github_button"
    //   icon={<GithubLogo className="" />}
    // />

    <Button
      className="w-full border py-6 bg-gray-300 rounded-lg"
      variant="soft"
      onClick={onPress}>
      <div className="flex items-center gap-2 noramp-text font-normal text-sm">
        <FaGithub className="" /> Continue with Github
      </div>
    </Button>
  );
};

export default GithubAuthButton;
